import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { api } from "../api";

type Post = {
    title: string;
    content: string;
    tags: string[];
    publishedAt: string;
    votes: number;
    slug: string;
};

const PostView = () => {
    const { slug } = useParams<{ slug: string }>();
    const [post, setPost] = useState<Post | null>(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [votes, setVotes] = useState(0);
    const [voted, setVoted] = useState<"up" | "down" | null>(null);

    useEffect(() => {
        api.getPost(slug!).then((data) => {
            if (data.error) setNotFound(true);
            else {
                setPost(data);
                setVotes(data.votes ?? 0);
                const stored = localStorage.getItem(`voted_${slug}`) as "up" | "down" | null;
                setVoted(stored);
            }
            setLoading(false);
        });
    }, [slug]);

    const handleVote = async (direction: "up" | "down") => {
        if (voted === direction) return;
        const result = await api.vote(slug!, direction);
        if (result.votes !== undefined) {
            setVotes(result.votes);
            setVoted(direction);
            localStorage.setItem(`voted_${slug}`, direction);
        }
    };

    if (loading) return (
        <div className="min-h-screen bg-(--bg-main) flex items-center justify-center">
            <p className="font-inter text-sm text-neutral-400">loading...</p>
        </div>
    );

    if (notFound) return (
        <div className="min-h-screen bg-(--bg-main) flex flex-col items-center justify-center gap-4">
            <p className="font-is text-4xl">post not found.</p>
            <Link to="/" className="font-inter text-sm text-[#F4611D]">← back to writing</Link>
        </div>
    );

    return (
        <div className="min-h-screen bg-(--bg-main) px-8 py-10 max-w-2xl mx-auto">
            <Link to="/" className="font-inter text-sm text-neutral-400 hover:text-neutral-600 transition-colors">
                ← back
            </Link>

            <article className="mt-12">
                <p className="font-inter text-xs text-neutral-400 mb-4">
                    {new Date(post!.publishedAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                </p>
                <h1 className="font-is text-5xl leading-tight">{post!.title}</h1>

                {post!.tags.length > 0 && (
                    <div className="flex gap-2 mt-4">
                        {post!.tags.map((tag) => (
                            <span key={tag} className="font-inter text-xs px-2 py-0.5 rounded-full border border-neutral-300 text-neutral-400">
                                {tag}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex gap-6 mt-10">
                    <div className="flex flex-col items-center gap-1 pt-1 sticky top-10 self-start">
                        <button
                            onClick={() => handleVote("up")}
                            disabled={voted === "up"}
                            className={`transition-colors cursor-pointer ${voted === "up" ? "text-[#F4611D]" : "text-neutral-300 hover:text-neutral-500"}`}
                            aria-label="upvote"
                        >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                <polygon points="10,3 19,17 1,17" />
                            </svg>
                        </button>
                        <span className="font-inter text-xs text-neutral-500 tabular-nums">{votes}</span>
                        <button
                            onClick={() => handleVote("down")}
                            disabled={voted === "down"}
                            className={`transition-colors cursor-pointer ${voted === "down" ? "text-[#F4611D]" : "text-neutral-300 hover:text-neutral-500"}`}
                            aria-label="downvote"
                        >
                            <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor">
                                <polygon points="10,17 19,3 1,3" />
                            </svg>
                        </button>
                    </div>

                    <div
                        className="prose flex-1 font-inter text-base font-light leading-relaxed text-neutral-700"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked(post!.content) as string) }}
                    />
                </div>
            </article>
        </div>
    );
};

export default PostView;
